/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: conferenceProfileApi.ts
 🗓️ Created: 2025-01-27
 🧩 Description:
    API functions for Conference Profiles.
    Centralizes all API calls related to Conference Profiles.
────────────────────────────────────────────────────────────────*/

import { buildApiUrl, getApiHeaders } from '../../../config/api';
import {
  ListConferenceProfilesResponse,
  ConferenceProfileResponse,
  ConferenceProfile,
} from '../types';

/**
 * Payload for creating a conference profile
 */
export interface CreateConferenceProfilePayload {
  name: string;
  description?: string;
  enabled?: boolean;
  settings?: Record<string, unknown>;
}

/**
 * Payload for updating a conference profile
 */
export interface UpdateConferenceProfilePayload {
  name?: string;
  description?: string;
  enabled?: boolean;
  settings?: Record<string, unknown>;
}

/**
 * Payload for duplicating a conference profile
 */
export interface DuplicateConferenceProfilePayload {
  name: string;
  description?: string;
}

/**
 * Fetch list of conference profiles
 */
export async function fetchConferenceProfiles(params: {
  page: number;
  limit: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  search?: string;
  enabled?: boolean;
  isSystem?: boolean;
  domainId?: number;
}): Promise<ListConferenceProfilesResponse> {
  const queryParams = new URLSearchParams({
    page: params.page.toString(),
    limit: params.limit.toString(),
    ...(params.sortBy && { sort: params.sortBy }),
    ...(params.sortOrder && { order: params.sortOrder }),
    ...(params.search && { search: params.search }),
    ...(params.enabled != null && { enabled: params.enabled.toString() }),
    ...(params.isSystem != null && { isSystem: params.isSystem.toString() }),
    ...(params.domainId != null && { domainId: params.domainId.toString() }),
  });

  const response = await fetch(
    buildApiUrl(`/api/telephony/conference-profiles?${queryParams}`),
    {
      headers: getApiHeaders(),
    }
  );

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(errorText || 'Failed to fetch conference profiles');
  }

  const result = await response.json();
  if (!result?.success) {
    throw new Error(result?.message || 'Failed to fetch conference profiles');
  }

  return result;
}

/**
 * Fetch all conference profiles (for quick list)
 */
export async function fetchAllConferenceProfiles(): Promise<ConferenceProfile[]> {
  const response = await fetch(
    buildApiUrl('/api/telephony/conference-profiles?page=1&limit=1000&sort=name&order=asc'),
    {
      headers: getApiHeaders(),
    }
  );

  if (!response.ok) {
    throw new Error('Failed to fetch conference profiles');
  }

  const result = await response.json();
  if (!result?.success) {
    throw new Error(result?.message || 'Failed to fetch conference profiles');
  }

  return result.data || [];
}

/**
 * Fetch single conference profile by ID
 */
export async function fetchConferenceProfile(id: string | number): Promise<ConferenceProfileResponse> {
  const response = await fetch(
    buildApiUrl(`/api/telephony/conference-profiles/${id}`),
    {
      headers: getApiHeaders(),
    }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
    throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
  }

  return await response.json();
}

/**
 * Generate XML for a conference profile
 */
export async function generateConferenceProfileXml(id: string | number): Promise<string> {
  const response = await fetch(
    buildApiUrl(`/api/telephony/conference-profiles/${id}/xml`),
    {
      headers: getApiHeaders(),
    }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
    throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
  }

  return await response.text();
}

/**
 * Create conference profile
 */
export async function createConferenceProfile(
  payload: CreateConferenceProfilePayload
): Promise<ConferenceProfileResponse> {
  const response = await fetch(
    buildApiUrl('/api/telephony/conference-profiles'),
    {
      method: 'POST',
      headers: getApiHeaders(),
      body: JSON.stringify(payload),
    }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
    throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
  }

  return await response.json();
}

/**
 * Update conference profile
 */
export async function updateConferenceProfile(
  id: string | number,
  payload: UpdateConferenceProfilePayload
): Promise<ConferenceProfileResponse> {
  const response = await fetch(
    buildApiUrl(`/api/telephony/conference-profiles/${id}`),
    {
      method: 'PUT',
      headers: getApiHeaders(),
      body: JSON.stringify(payload),
    }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
    throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
  }

  return await response.json();
}

/**
 * Delete conference profile
 */
export async function deleteConferenceProfile(id: string | number): Promise<void> {
  const response = await fetch(
    buildApiUrl(`/api/telephony/conference-profiles/${id}`),
    {
      method: 'DELETE',
      headers: getApiHeaders(),
    }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
    throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
  }
}

/**
 * Duplicate conference profile
 */
export async function duplicateConferenceProfile(
  id: string | number,
  payload: DuplicateConferenceProfilePayload
): Promise<ConferenceProfileResponse> {
  const response = await fetch(
    buildApiUrl(`/api/telephony/conference-profiles/${id}/duplicate`),
    {
      method: 'POST',
      headers: getApiHeaders(),
      body: JSON.stringify(payload),
    }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
    throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
  }

  return await response.json();
}

